import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "../../lib/api";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [["serum", "Sérum"], ["variant", "Variante"], ["coffret", "Coffret"]];
const UNIVERSES = [["all", "Tous"], ["femme", "Femme"], ["homme", "Homme"], ["mixte", "Mixte"]];

const EMPTY = {
  slug: "", category: "coffret", name: "", subtitle: "", description: "",
  price: 0, lookup_key: "", stock: 0, active: true, universe: "all", image: "", badge: "", sort_order: 9,
};

function imgSrc(v) {
  if (!v) return "";
  return v.startsWith("http") || v.startsWith("/assets") ? v : `${BACKEND_URL}${v}`;
}

function ImageField({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <span className="overline block mb-1" style={{ fontSize: "0.55rem" }}>Image (URL ou téléversement)</span>
      <div className="flex items-center gap-3">
        {value && <img src={imgSrc(value)} alt="" className="w-12 h-12 object-cover shrink-0" style={{ border: "1px solid var(--lo-border)" }} />}
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="/assets/… ou URL" className="flex-1 bg-transparent border-b py-1 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid={testid} />
        <label className="btn-ghost h-9 px-4 text-xs cursor-pointer whitespace-nowrap" data-testid={`${testid}-upload`}>
          {uploading ? "…" : "Téléverser"}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
      </div>
    </div>
  );
}

function ProductCard({ p, onSaved, onDeleted }) {
  const [d, setD] = useState(p);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD({ ...d, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...d, price: Number(d.price), stock: Number(d.stock), sort_order: Number(d.sort_order) };
      const { data } = await api.put(`/admin/products/${p.id}`, payload);
      onSaved(data);
      toast.success(`${data.name} mis à jour`);
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    onDeleted(p.id);
    toast.success("Produit supprimé");
  };

  return (
    <div className="card-lux p-5" data-testid={`product-${d.slug}`}>
      <div className="flex gap-4">
        {d.image && <img src={imgSrc(d.image)} alt={d.name} className="w-20 h-20 object-cover shrink-0" style={{ border: "1px solid var(--lo-border)" }} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="badge-save text-[9px] px-2 py-0.5 uppercase">{d.category}</span>
            <span className="overline" style={{ fontSize: "0.55rem", color: "var(--lo-muted)" }}>{d.lookup_key}</span>
          </div>
          <input value={d.name} onChange={(e) => set("name", e.target.value)} className="font-display text-lg bg-transparent w-full outline-none border-b" style={{ borderColor: "transparent" }} data-testid="product-name" />
        </div>
        <button onClick={del} className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: "var(--lo-border)" }} data-testid="product-delete"><Trash2 size={15} /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
        <label className="block"><span className="overline block mb-1" style={{ fontSize: "0.55rem" }}>Prix (€)</span>
          <input type="number" step="0.01" value={d.price} onChange={(e) => set("price", e.target.value)} className="w-full bg-transparent border-b py-1 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="product-price" /></label>
        <label className="block"><span className="overline block mb-1" style={{ fontSize: "0.55rem" }}>Stock</span>
          <input type="number" value={d.stock} onChange={(e) => set("stock", e.target.value)} className="w-full bg-transparent border-b py-1 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="product-stock" /></label>
        <label className="block"><span className="overline block mb-1" style={{ fontSize: "0.55rem" }}>Univers</span>
          <select value={d.universe} onChange={(e) => set("universe", e.target.value)} className="w-full bg-transparent border-b py-1 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }}>
            {UNIVERSES.map(([v, l]) => <option key={v} value={v} style={{ background: "#111" }}>{l}</option>)}
          </select></label>
        <label className="flex items-center gap-2 pt-4"><input type="checkbox" checked={d.active} onChange={(e) => set("active", e.target.checked)} data-testid="product-active" /><span className="overline" style={{ fontSize: "0.6rem" }}>Actif</span></label>
      </div>
      <label className="block mt-3"><span className="overline block mb-1" style={{ fontSize: "0.55rem" }}>Description</span>
        <textarea value={d.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full bg-transparent border py-2 px-3 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} /></label>
      <div className="grid grid-cols-2 gap-4 mt-3">
        <ImageField value={d.image} onChange={(v) => set("image", v)} testid="product-image" />
        <label className="block"><span className="overline block mb-1" style={{ fontSize: "0.55rem" }}>Badge</span>
          <input value={d.badge} onChange={(e) => set("badge", e.target.value)} className="w-full bg-transparent border-b py-1 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} /></label>
      </div>
      <button onClick={save} disabled={busy} className="btn-primary h-10 px-6 mt-4 disabled:opacity-50" data-testid="product-save">{busy ? "…" : "Enregistrer"}</button>
    </div>
  );
}

export function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [np, setNp] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!np.name || !np.slug || !np.lookup_key) { toast.error("Nom, slug et clé Stripe requis"); return; }
    setBusy(true);
    try {
      const payload = { ...np, price: Number(np.price), stock: Number(np.stock), sort_order: Number(np.sort_order) };
      const { data } = await api.post("/admin/products", payload);
      setProducts((p) => [...p, data]);
      setAdding(false); setNp(EMPTY);
      toast.success("Produit créé");
    } catch { toast.error("Erreur de création"); }
    finally { setBusy(false); }
  };

  const onSaved = (updated) => setProducts((p) => p.map((x) => (x.id === updated.id ? updated : x)));
  const onDeleted = (id) => setProducts((p) => p.filter((x) => x.id !== id));

  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="flex justify-end">
        <button onClick={() => setAdding((v) => !v)} className="btn-ghost inline-flex items-center gap-2 h-10 px-5" data-testid="product-add-toggle"><Plus size={15} /> Nouveau produit</button>
      </div>
      {adding && (
        <div className="card-lux p-5" data-testid="product-new-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <input placeholder="Nom" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="new-name" />
            <input placeholder="slug-unique" value={np.slug} onChange={(e) => setNp({ ...np, slug: e.target.value })} className="bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="new-slug" />
            <input placeholder="cle_stripe" value={np.lookup_key} onChange={(e) => setNp({ ...np, lookup_key: e.target.value })} className="bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="new-lookup" />
            <input type="number" step="0.01" placeholder="Prix" value={np.price} onChange={(e) => setNp({ ...np, price: e.target.value })} className="bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="new-price" />
            <input type="number" placeholder="Stock" value={np.stock} onChange={(e) => setNp({ ...np, stock: e.target.value })} className="bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} />
            <select value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} className="bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }}>
              {CATEGORIES.map(([v, l]) => <option key={v} value={v} style={{ background: "#111" }}>{l}</option>)}
            </select>
          </div>
          <div className="mt-4 max-w-md">
            <ImageField value={np.image} onChange={(v) => setNp({ ...np, image: v })} testid="new-image" />
          </div>
          <button onClick={create} disabled={busy} className="btn-primary h-10 px-6 mt-4 disabled:opacity-50" data-testid="new-create">{busy ? "…" : "Créer"}</button>
        </div>
      )}
      {products.map((p) => <ProductCard key={p.id} p={p} onSaved={onSaved} onDeleted={onDeleted} />)}
    </div>
  );
}
